import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MENU, MENU_CATEGORIES } from "@/data/content";
import { Eyebrow, Reveal } from "./Reveal";

const EASE = [0.16, 1, 0.3, 1];

const MenuItem = ({ item, index }) => (
  <motion.li
    data-testid={`menu-item-${index}`}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.55, delay: 0.05 + index * 0.05, ease: EASE }}
    className="group"
  >
    <div className="flex items-baseline">
      <h3 className="font-display text-lg font-semibold text-[#1B3B2B] transition-colors duration-300 group-hover:text-[#C85A32] sm:text-xl">
        {item.name}
      </h3>
      <span className="mx-3 flex-1 -translate-y-1 border-b border-dotted border-[#1B3B2B]/25" />
      <span className="font-display text-lg font-semibold text-[#C85A32]">{item.price}</span>
    </div>
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <p className="text-sm font-light text-[#3D5A4B]/85">{item.desc}</p>
      {item.tags?.map((tag) => (
        <span
          key={tag}
          className="rounded-full border border-[#C85A32]/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[#C85A32]"
        >
          {tag}
        </span>
      ))}
    </div>
  </motion.li>
);

export const MenuSection = () => {
  const [active, setActive] = useState(MENU_CATEGORIES[0].id);
  const category = MENU_CATEGORIES.find((c) => c.id === active);

  return (
    <section
      id="la-carte"
      data-testid="menu-section"
      className="border-y border-[#1B3B2B]/10 bg-[#F4EFE6] py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <Reveal>
              <Eyebrow>La carte</Eyebrow>
            </Reveal>
            <Reveal delay={0.1}>
              <h2
                data-testid="menu-title"
                className="mt-6 max-w-lg font-display text-3xl font-semibold tracking-tight text-[#1B3B2B] sm:text-4xl lg:text-5xl"
              >
                Une carte courte,
                <br />
                de <em className="italic text-[#C85A32]">saison</em>.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-xs text-sm font-light leading-relaxed text-[#3D5A4B]/85">
              Prix en euros, taxes comprises. La carte du déjeuner change selon
              l'arrivage du marché de la place des Lices.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="menu-tabs"
            className="mt-12 flex flex-wrap gap-2.5"
            role="tablist"
            aria-label="Catégories de la carte"
          >
            {MENU_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={active === c.id}
                data-testid={`menu-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`rounded-full px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                  active === c.id
                    ? "bg-[#1B3B2B] text-[#F5F0E6]"
                    : "border border-[#1B3B2B]/20 bg-transparent text-[#1B3B2B]/75 hover:border-[#1B3B2B]/50 hover:text-[#1B3B2B]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.ul
                key={active}
                data-testid="menu-items-list"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="space-y-7"
              >
                {MENU[active].map((item, i) => (
                  <MenuItem key={item.name} item={item} index={i} />
                ))}
              </motion.ul>
            </AnimatePresence>
          </div>

          <div className="hidden lg:col-span-4 lg:col-start-9 lg:block">
            <div className="sticky top-28">
              <div className="relative aspect-[4/5] overflow-hidden rounded-b-[24px] rounded-t-[999px] shadow-[0_24px_60px_rgba(27,59,43,0.16)]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={category.image}
                    data-testid="menu-category-image"
                    src={category.image}
                    alt={category.label}
                    loading="lazy"
                    initial={{ opacity: 0, scale: 1.08 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </AnimatePresence>
                <div className="pointer-events-none absolute inset-0 rounded-b-[24px] rounded-t-[999px] ring-1 ring-inset ring-[#1B3B2B]/10" />
              </div>
              <p
                data-testid="menu-category-note"
                className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#C85A32]"
              >
                {category.note}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
